/**
 * TopperSelect — "Paul video" click-to-load speler (Horizon theme)
 * Bij paginalaad staat alleen de poster + afspeelknop in de DOM. De video
 * wacht inert in een <template>. Zodra de sectie in beeld komt, zetten we hem
 * alvast verborgen klaar met preload=metadata (snelle start, ook op mobiel).
 * Na de tik (op de knop óf op de poster) tonen we de speler meteen en
 * starten we het afspelen; de native controls vangen de rest op.
 */
const IFRAME_REVEAL_TIMEOUT = 2500;

class PaulVideo extends HTMLElement {
  #abort = null;
  #node = null;
  #loaded = false;
  #revealed = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const sig = { signal: this.#abort.signal };

    this.media = this.querySelector('[data-pv-media]');
    this.playBtn = this.querySelector('[data-pv-play]');
    this.tpl = this.querySelector('[data-pv-template]');
    this.fallback = this.querySelector('[data-pv-fallback]');

    if (!this.playBtn || !this.media) return;

    this.playBtn.addEventListener('click', (e) => { e.preventDefault(); this.#load(); }, sig);
    // Een tik op de poster zelf telt ook als "afspelen".
    this.media.addEventListener('click', (e) => {
      if (this.#loaded || e.target.closest('[data-pv-play]')) return;
      this.#load();
    }, sig);

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (entries.some((en) => en.isIntersecting)) {
          io.disconnect();
          this.#prepare();
        }
      }, { rootMargin: '300px' });
      io.observe(this);
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#node = null;
    this.#loaded = false;
    this.#revealed = false;
  }

  #showFallback() {
    this.fallback?.setAttribute('data-visible', '');
    this.playBtn?.remove();
  }

  /** Poster en knop weghalen zodra de speler klaarstaat. Idempotent. */
  #reveal() {
    if (this.#revealed) return;
    this.#revealed = true;
    this.media.querySelectorAll('[data-pv-poster]').forEach((node) => node.remove());
    this.playBtn?.remove();
  }

  /** Video alvast verborgen in de DOM zetten en metadata laden. */
  #prepare() {
    if (this.#node || this.#loaded) return null;
    const node = this.tpl?.content?.firstElementChild?.cloneNode(true);
    if (!node || node.tagName === 'IFRAME') return null;
    const video = node.tagName === 'VIDEO' ? node : node.querySelector('video');
    if (!video) return null;

    // Altijd de hoogste rendition: bij staande video's is de "720p"-versie maar
    // 404 pixels breed en wordt die op een telefoon flink uitvergroot.
    const poster = this.media.querySelector('[data-pv-poster]');
    const posterSrc = poster && (poster.currentSrc || poster.getAttribute('src'));
    if (posterSrc && !video.getAttribute('poster')) video.setAttribute('poster', posterSrc);

    video.setAttribute('preload', 'metadata');
    video.setAttribute('playsinline', '');
    video.setAttribute('controls', '');
    video.addEventListener('error', () => this.#showFallback(), { once: true });
    node.hidden = true;
    this.media.appendChild(node);
    this.#node = node;
    try { video.load(); } catch (e) { /* niets */ }
    return node;
  }

  #load() {
    if (this.#loaded) return;

    const tplNode = this.tpl?.content?.firstElementChild;
    if (!tplNode) {
      this.#showFallback();
      return;
    }

    if (tplNode.tagName === 'IFRAME') {
      this.#loaded = true;
      const node = tplNode.cloneNode(true);
      node.addEventListener('load', () => this.#reveal(), { once: true });
      node.addEventListener('error', () => this.#showFallback(), { once: true });
      this.media.appendChild(node);
      node.src = node.getAttribute('data-pv-src');
      setTimeout(() => this.#reveal(), IFRAME_REVEAL_TIMEOUT);
      return;
    }

    const node = this.#node || this.#prepare();
    if (!node) {
      this.#showFallback();
      return;
    }
    this.#loaded = true;
    const video = node.tagName === 'VIDEO' ? node : node.querySelector('video');

    // Speler meteen tonen; de poster zit als poster-attribuut op de video, dus
    // er is geen leeg vlak terwijl de eerste seconden binnenkomen.
    node.hidden = false;
    this.#reveal();

    const played = video.play();
    if (played && typeof played.catch === 'function') {
      played.catch(() => { /* geblokkeerd: de bezoeker start via de controls */ });
    }
  }
}

if (!customElements.get('paul-video')) {
  customElements.define('paul-video', PaulVideo);
}
